import { ThumbnailsPosition } from 'libs/shared/src/models';
import { GsFormField } from '../../../../../components';
import { DEFAULT_CONFIG } from '../../../../../services/config/default.config';
import { BASIC_SCHEMA_FIELDS } from '../../../basics/basic.schema';
import { borderRadiusSchemaFields, borderSchemaFields } from '../../../basics/border.schema';
import { buttonSchemaFields } from '../../../basics/button-fields.schema';
import { pictureDisplayModeSchemaFor } from '../../../basics/picture-display-mode.schema';
import { SPACING_SCHEMA } from '../../spacing.schema';
import { Mode } from '../../../basics';

export const ARTICLE_INFO_REFERENCES_SCHEMA: GsFormField<unknown>[] = [
  {
    formControlName: 'showReferences',
    label: 'components.articleInfo.schema.references.showReferences.label',
    description: 'components.articleInfo.schema.references.showReferences.description',
    type: 'checkbox',
    tabName: 'content',
    default: false,
    groupingName: 'components.articleInfo.schema.references.groupingName'
  },
  {
    formControlName: 'referencesShowWatchlistButton',
    label: 'components.articleInfo.schema.references.referencesShowWatchlistButton.label',
    type: 'checkbox',
    tabName: 'content',
    default: false,
    groupingName: 'components.articleInfo.schema.references.groupingName',
    if: {
      args: [
        {
          formControlName: 'showReferences',
          operator: '==',
          value: true
        }
      ]
    }
  }
];

/** Grouping for the variant-display and delivery-method settings, which belong together for the editor */
const VARIANTS_SHIPPING_GROUPING_NAME = 'components.articleInfo.schema.variantsAndShipping.groupingName';

export const ARTICLE_INFO_SLAVE_DISPLAY_SCHEMA: GsFormField<unknown>[] = [
  {
    formControlName: 'slavePropertyDisplayMode',
    label: 'components.articleInfo.schema.slavePropertyDisplayMode.label',
    description: 'components.articleInfo.schema.slavePropertyDisplayMode.description',
    type: 'select',
    valueType: 'string',
    // Same tab as the backend "Eigenschaften" (SelectionArticlePropertyKeys) setting, which has no tabSheet and lands in the "general" fallback tab
    tabName: 'general',
    groupingName: VARIANTS_SHIPPING_GROUPING_NAME,
    default: 'buttons',
    options: [
      { text: 'components.articleInfo.schema.slavePropertyDisplayMode.options.buttons', value: 'buttons' },
      { text: 'components.articleInfo.schema.slavePropertyDisplayMode.options.dropdown', value: 'dropdown' }
    ]
  }
];

export const ARTICLE_INFO_UNAVAILABLE_VARIANTS_SCHEMA: GsFormField<unknown>[] = [
  {
    formControlName: 'unavailableVariantDisplayMode',
    label: 'components.articleInfo.schema.unavailableVariantDisplayMode.label',
    description: 'components.articleInfo.schema.unavailableVariantDisplayMode.description',
    type: 'select',
    valueType: 'string',
    tabName: 'general',
    groupingName: VARIANTS_SHIPPING_GROUPING_NAME,
    default: 'grayout',
    options: [
      { text: 'components.articleInfo.schema.unavailableVariantDisplayMode.options.normal', value: 'normal' },
      { text: 'components.articleInfo.schema.unavailableVariantDisplayMode.options.hide', value: 'hide' },
      { text: 'components.articleInfo.schema.unavailableVariantDisplayMode.options.grayout', value: 'grayout' }
    ]
  }
];

/** Which buy button the PDP shows (only the standard article info renders the inquiry button). */
export const ARTICLE_INFO_PURCHASE_BUTTON_SCHEMA: GsFormField<unknown>[] = [
  {
    formControlName: 'purchaseButtonMode',
    label: 'components.articleInfo.schema.purchaseButtonMode.label',
    description: 'components.articleInfo.schema.purchaseButtonMode.description',
    type: 'select',
    valueType: 'string',
    default: 'auto',
    options: [
      { text: 'components.articleInfo.schema.purchaseButtonMode.options.auto', value: 'auto' },
      { text: 'components.articleInfo.schema.purchaseButtonMode.options.checkout', value: 'checkout' },
      { text: 'components.articleInfo.schema.purchaseButtonMode.options.inquiry', value: 'inquiry' }
    ]
  }
];

/** Toggle for the "Erläuterung der Varianten und Qualitäten" link (only rendered when a page is configured on the article). */
export const ARTICLE_INFO_QUALITY_EXPLANATION_SCHEMA: GsFormField<unknown>[] = [
  {
    formControlName: 'showQualityExplanation',
    label: 'components.articleInfo.schema.showQualityExplanation.show',
    description: 'components.articleInfo.schema.showQualityExplanation.description',
    type: 'checkbox',
    tabName: 'general',
    groupingName: VARIANTS_SHIPPING_GROUPING_NAME,
    default: true
  }
];

export const ARTICLE_INFO_THUMBNAILS_POSITION_SCHEMA: GsFormField<unknown>[] = [
  {
    formControlName: 'thumbnailsPosition',
    label: 'components.articleInfo.schema.thumbnailsPosition.label',
    type: 'select',
    valueType: 'number',
    default: ThumbnailsPosition.LEFT,
    options: [
      { text: 'components.articleInfo.schema.thumbnailsPosition.options.left', value: ThumbnailsPosition.LEFT },
      { text: 'components.articleInfo.schema.thumbnailsPosition.options.bottom', value: ThumbnailsPosition.BOTTOM }
    ]
  }
];

export const ARTICLE_INFO_IMAGE_HEIGHT_SCHEMA: GsFormField<unknown>[] = [
  {
    formControlName: 'imageHeight',
    label: 'components.articleInfo.schema.imageHeight.label',
    type: 'number',
    default: {
      default: 430,
      lg: 880
    },
    min: 200,
    max: 1000,
    isResponsive: true,
    nullable: false,
    description: 'components.articleInfo.schema.imageHeight.description'
  }
];

/** Separate Display-Mode-Felder fürs Hauptbild (Vorschau) und für den Thumb-Swiper. */
export const ARTICLE_INFO_PICTURE_DISPLAY_MODE_SCHEMA: GsFormField<unknown>[] = [
  pictureDisplayModeSchemaFor(
    'previewPictureDisplayMode',
    'components.articleInfo.schema.previewPictureDisplayMode.label',
    'components.articleInfo.schema.previewPictureDisplayMode.description'
  ),
  pictureDisplayModeSchemaFor(
    'swiperPictureDisplayMode',
    'components.articleInfo.schema.swiperPictureDisplayMode.label',
    'components.articleInfo.schema.swiperPictureDisplayMode.description'
  )
];

/**
 * The schema for the image component form.
 * Defines the fields to display for the form.
 */
export const ARTICLE_INFO_BASE_FORM_SCHEMA: GsFormField<unknown>[] = [
  ...BASIC_SCHEMA_FIELDS,
  ...SPACING_SCHEMA,
  ...ARTICLE_INFO_IMAGE_HEIGHT_SCHEMA,
  ...ARTICLE_INFO_PICTURE_DISPLAY_MODE_SCHEMA,
  ...borderSchemaFields('ItemImage', undefined, 'schema.border.name'),
  ...borderRadiusSchemaFields(
    'SwiperImage',
    undefined,
    'components.articleInfo.schema.swiperImage.groupingName',
    undefined,
    'components.articleInfo.schema.swiperImage',
    true
  ),
  {
    formControlName: 'newForDays',
    label: 'components.articleInfo.schema.newForDays.label',
    description: 'components.articleInfo.schema.newForDays.description',
    type: 'number',
    tabName: 'general',
    default: 10,
    min: 0
  },
  {
    formControlName: 'buttons',
    label: 'components.articleInfo.schema.buttons',
    type: 'group',
    expandable: true,
    default: DEFAULT_CONFIG.uiElements?.buttons ?? {},
    fields: buttonSchemaFields()
  },
  {
    formControlName: 'ratingsMode',
    label: 'components.articleInfo.schema.ratingsMode.label',
    type: 'select',
    valueType: 'number',
    default: Mode.GRID,
    options: [
      { text: 'schema.displayMode.grid', value: Mode.GRID },
      { text: 'schema.displayMode.slider', value: Mode.SLIDER }
    ]
  },
  {
    formControlName: 'showAvailabilityDetails',
    label: 'components.articleInfo.schema.showAvailabilityDetails.show',
    description: 'components.articleInfo.schema.showAvailabilityDetails.description',
    type: 'checkbox',
    default: false
  },
  {
    formControlName: 'showAvailabilityNotification',
    label: 'components.articleInfo.schema.showAvailabilityNotification.label',
    description: 'components.articleInfo.schema.showAvailabilityNotification.description',
    type: 'checkbox',
    default: false
  },
  {
    formControlName: 'showTieredPrices',
    label: 'components.articleInfo.schema.showTieredPrices.show',
    description: 'components.articleInfo.schema.showTieredPrices.description',
    type: 'checkbox',
    default: false
  },
  {
    formControlName: 'hideShippingMethod',
    label: 'components.articleInfo.schema.hideShippingMethod.label',
    description: 'components.articleInfo.schema.hideShippingMethod.description',
    type: 'checkbox',
    tabName: 'general',
    groupingName: VARIANTS_SHIPPING_GROUPING_NAME,
    default: false
  }
];
